import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { toast } from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from 'shared/components/Header';
import { useAuth } from 'shared/hooks/useAuth';
import useProfilePhotoSrc from 'shared/hooks/useProfilePhotoSrc';
import { sessionHasPermission } from 'shared/utils/sessionPermissions';
import { fetchActiveTenantUsers } from 'shared/api/services/tenantUserService';
import {
  getCachedJobPosition,
  getRegistryTenantIdFromSession,
  profileJobPosition,
} from 'shared/utils/sessionProfileStore';
import { accessProvisionApi } from '../../api/systemRegisterApi';
import { formatEnumLabel } from '../../constants/enums';
import DeploymentStageApprovalDialog from '../../shared/DeploymentStageApprovalDialog';
import { apiErrorMessage, unwrapData } from '../../utils/responseHelpers';
import {
  getReportFormPalette as getFormPalette,
  REPORT_PRINT_KEEP_CSS,
  reportHeaderPrintSx,
  reportKeepTogetherSx,
} from 'shared/utils/reportFormPalette';
import { downloadFormPdf } from 'shared/utils/downloadFormPdf';

const HEADER_BLUE = '#0D2240';
const FORM_PRINT_ROOT_ID = 'access-provision-form';
const PRINTING_BODY_CLASS = 'sr-printing-access-provision-form';

const FIRST_ACCESS_APPROVAL_PERMISSION = 'PERFORM_FIRST_ACCESS_PROVISION_APPROVAL';
const SECOND_ACCESS_APPROVAL_PERMISSION = 'PERFORM_SECOND_ACCESS_PROVISION_APPROVAL';

function isAdminRole(user) {
  const role = user?.data?.role;
  const normalized = String(role ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s_-]+/g, '');
  return normalized === 'ADMIN';
}

function dash(value) {
  const v = typeof value === 'string' ? value.trim() : value;
  return v || v === 0 ? String(v) : '';
}

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function slotRole(slot) {
  return slot === 1 ? '1st Approval' : '2nd Approval';
}

function normalizePersonKey(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function jobPositionForApproval(approval, users, currentUser) {
  const stored = approval?.jobPosition && String(approval.jobPosition).trim();
  if (stored) return stored;
  const email = normalizePersonKey(approval?.signatureConfirmation || approval?.approverEmail);
  const name = normalizePersonKey(approval?.approverName);
  const cached = getCachedJobPosition({
    email: approval?.signatureConfirmation || approval?.approverEmail,
    name: approval?.approverName,
  });
  if (cached) return cached;
  const match =
    (email && users.find((u) => normalizePersonKey(u.email) === email))
    || (name && users.find((u) => normalizePersonKey(u.name) === name))
    || null;
  if (match?.jobPosition) return String(match.jobPosition).trim();
  if (
    currentUser
    && (
      (email && email === normalizePersonKey(currentUser.email))
      || (name && name === normalizePersonKey(currentUser.name))
    )
    && currentUser.jobPosition
  ) {
    return String(currentUser.jobPosition).trim();
  }
  return '';
}

function findSlotApproval(approvals, slot) {
  const role = slotRole(slot);
  return (approvals ?? []).find((a) => a?.approverRole === role || Number(a?.slot) === slot) || null;
}

function approvalDecisionLabel(approval) {
  if (!approval?.approvalStatus) return null;
  if (approval.approvalStatus === 'APPROVED') return 'APPROVED';
  if (approval.approvalStatus === 'REJECTED') return 'DECLINED';
  return formatEnumLabel(approval.approvalStatus);
}

const FieldCell = ({ label, children, sx, colSpan, border, muted }) => (
  <TableCell
    colSpan={colSpan}
    sx={{
      border,
      p: 1.25,
      verticalAlign: 'top',
      color: 'inherit',
      ...sx,
    }}
  >
    <Typography
      component="span"
      sx={{ fontWeight: 700, fontSize: '0.78rem', display: 'block', mb: 0.5, color: 'inherit' }}
    >
      {label}
    </Typography>
    <Box sx={{ minHeight: 22, fontSize: '0.9rem', color: 'inherit' }}>
      {children || <Box sx={{ color: muted, opacity: 0.7 }}>&nbsp;</Box>}
    </Box>
  </TableCell>
);

export default function AccessProvisionViewPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const queryId = searchParams.get('id');
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogSlot, setDialogSlot] = useState(1);
  const [tenantUsers, setTenantUsers] = useState([]);

  useEffect(() => {
    if (!queryId) {
      setRecord(null);
      setLoadError('Access request id is missing.');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const response = await accessProvisionApi.get(queryId);
        if (cancelled) return;
        setRecord(unwrapData(response));
      } catch (error) {
        if (!cancelled) {
          setRecord(null);
          setLoadError(apiErrorMessage(error, 'Failed to load access provision details.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [queryId]);

  useEffect(() => {
    let cancelled = false;
    const loadUsers = async () => {
      const registryTenantId = getRegistryTenantIdFromSession();
      if (!registryTenantId) return;
      try {
        const rows = await fetchActiveTenantUsers(registryTenantId, { page: 0, size: 500 });
        if (!cancelled) setTenantUsers(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setTenantUsers([]);
      }
    };
    loadUsers();
    return () => {
      cancelled = true;
    };
  }, []);

  const profile = user?.data;
  const tenantBranding = profile?.tenantBranding;
  const isAdmin = isAdminRole(user);
  const canFirstApproval =
    isAdmin || sessionHasPermission(profile, FIRST_ACCESS_APPROVAL_PERMISSION);
  const canSecondApproval =
    isAdmin || sessionHasPermission(profile, SECOND_ACCESS_APPROVAL_PERMISSION);
  const form = useMemo(
    () => getFormPalette(isDark, tenantBranding),
    [isDark, tenantBranding?.backgroundColor, tenantBranding?.primaryColor, tenantBranding?.secondaryColor]
  );
  const tenantLogoSrc = useProfilePhotoSrc(tenantBranding?.tenantPhoto);
  const tenantDisplayName =
    (profile?.tenantName && String(profile.tenantName).trim())
    || (tenantBranding?.name && String(tenantBranding.name).trim())
    || 'Organisation';

  const currentUser = useMemo(
    () => ({
      name: profile?.name?.trim() || profile?.fullName?.trim() || '',
      email: profile?.email?.trim() || '',
      jobPosition:
        profileJobPosition(profile)
        || getCachedJobPosition({
          id: profile?.id,
          email: profile?.email,
          name: profile?.name || profile?.fullName,
        }),
    }),
    [profile]
  );

  const approvals = record?.approvals ?? [];
  const firstApproval = findSlotApproval(approvals, 1);
  const secondApproval = findSlotApproval(approvals, 2);
  const firstApproved = firstApproval?.approvalStatus === 'APPROVED';

  const openApprovalDialog = (slot) => {
    const allowed = slot === 1 ? canFirstApproval : canSecondApproval;
    if (!allowed) {
      toast.error('You do not have permission for this approval.');
      return;
    }
    if (slot === 2 && !firstApproved) {
      toast.error('First approval is required before second approval.');
      return;
    }
    if (!currentUser.name && !currentUser.email) {
      toast.error('Logged-in user name or email is required to approve or decline.');
      return;
    }
    setDialogSlot(slot);
    setDialogOpen(true);
  };

  const handleApprovalSubmit = async ({ decision, comment }) => {
    if (!record?.id) return;
    setSaving(true);
    try {
      const response = await accessProvisionApi.recordApproval(record.id, {
        slot: dialogSlot,
        name: currentUser.name || currentUser.email,
        email: currentUser.email || '',
        jobPosition: currentUser.jobPosition || '',
        approvalDate: new Date().toISOString().slice(0, 10),
        decision,
        comment,
      });
      setRecord(unwrapData(response));
      toast.success(
        decision === 'DECLINED'
          ? `${slotRole(dialogSlot)} declined.`
          : `${slotRole(dialogSlot)} approved.`
      );
      setDialogOpen(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save access provision approval.'));
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = useCallback(() => {
    const previousTitle = document.title;
    document.title = ' ';
    const cleanup = () => {
      document.title = previousTitle;
      document.body.classList.remove(PRINTING_BODY_CLASS);
      window.removeEventListener('afterprint', cleanup);
    };
    document.body.classList.add(PRINTING_BODY_CLASS);
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(cleanup, 1500);
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    if (downloadingPdf || !record) return;
    const formEl = document.getElementById(FORM_PRINT_ROOT_ID);
    if (!formEl) return;
    setDownloadingPdf(true);
    try {
      const labelBase = (record.requestedBy || 'access-provision')
        .replace(/[^\w\-]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
        .slice(0, 48) || 'access-provision';
      await downloadFormPdf({
        element: formEl,
        fileName: `${labelBase}-ACCESS-ADMINISTRATION.pdf`,
      });
      toast.success('PDF downloaded.');
    } catch (error) {
      console.error(error);
      toast.error('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  }, [downloadingPdf, record]);

  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-sr-access-provision-form-print', 'true');
    style.textContent = `
      @page { size: A4; margin: 0; }
      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body.${PRINTING_BODY_CLASS} .no-print { display: none !important; }
        body.${PRINTING_BODY_CLASS} * { visibility: hidden !important; }
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID},
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID} * {
          visibility: visible !important;
        }
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID} {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          margin: 0 !important;
          box-shadow: none !important;
        }
        ${REPORT_PRINT_KEEP_CSS}
      }
    `;
    document.head.appendChild(style);
    return () => {
      style.remove();
      document.body.classList.remove(PRINTING_BODY_CLASS);
    };
  }, []);

  const renderApprovalButton = (slot, approval) => {
    const allowed = slot === 1 ? canFirstApproval : canSecondApproval;
    if (!allowed) return null;
    const decision = approvalDecisionLabel(approval);
    const label = slotRole(slot);
    const blockedSecond = slot === 2 && !firstApproved;
    return (
      <Button
        key={slot}
        variant={decision === 'APPROVED' ? 'contained' : 'outlined'}
        color={decision === 'DECLINED' ? 'error' : decision === 'APPROVED' ? 'success' : 'secondary'}
        onClick={() => openApprovalDialog(slot)}
        disabled={saving || blockedSecond}
        sx={{ textTransform: 'none' }}
      >
        {decision ? `${label} (${decision})` : label}
      </Button>
    );
  };

  const renderApprovalCell = () => {
    if (!firstApproval && !secondApproval) {
      return (
        <Typography sx={{ fontSize: '0.85rem', color: 'inherit', opacity: 0.7, pl: 2 }}>
          —
        </Typography>
      );
    }
    return (
      <Box display="flex" flexDirection="column" gap={1.5} sx={{ pl: 2, pr: 1.5, py: 1 }}>
        {[firstApproval, secondApproval].filter(Boolean).map((a) => {
          const roleLabel = a.approverRole || (Number(a.slot) === 2 ? '2nd Approval' : '1st Approval');
          const signatureEmail = a.signatureConfirmation || a.approverEmail || '';
          const jobPosition = jobPositionForApproval(a, tenantUsers, currentUser);
          return (
            <Box
              key={a.approverRole || a.slot || a.id}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                columnGap: 4,
                rowGap: 0.75,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'inherit' }}>
                  {roleLabel}
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', color: 'inherit' }}>
                  {a.approverName || '—'}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: 'inherit' }}>
                  {approvalDecisionLabel(a) || '—'}
                  {a.approvalDate ? ` — ${a.approvalDate}` : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: '0.75rem', color: 'inherit', opacity: 0.75 }}>
                  Signature
                </Typography>
                <Typography
                  sx={{
                    fontFamily:
                      '"Segoe Script", "Lucida Handwriting", "Bradley Hand", "Comic Sans MS", cursive',
                    fontSize: '1.05rem',
                    lineHeight: 1.3,
                    color: 'inherit',
                  }}
                >
                  {jobPosition || '—'}
                </Typography>
                {signatureEmail ? (
                  <Typography sx={{ fontSize: '0.75rem', color: 'inherit', opacity: 0.85 }}>
                    {signatureEmail}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }} display="flex" justifyContent="center" py={8}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  if (!record) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }}>
        <Header title="Access Administration" />
        <Paper variant="outlined" sx={{ p: 3, mt: 2, borderRadius: 2 }}>
          <Typography variant="body1" mb={2}>
            {loadError || 'Open an access request from Access Administration using the view action.'}
          </Typography>
          <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            Go back
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      m={{ xs: '8px', sm: '12px' }}
      sx={{
        height: 'calc(100vh - 96px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        '@media print': {
          height: 'auto',
          m: 0,
          bgcolor: '#fff',
          '& .no-print': { display: 'none !important' },
        },
      }}
    >
      <Box className="no-print">
        <Header title="Access Administration" />
      </Box>

      <Box
        className="no-print"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        mb={2}
      >
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          {renderApprovalButton(1, firstApproval)}
          {renderApprovalButton(2, secondApproval)}
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
        <Button type="button" startIcon={<PrintOutlinedIcon />} onClick={handlePrint} variant="outlined">
          Print
        </Button>
        <Button
          type="button"
          startIcon={
            downloadingPdf ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfOutlinedIcon />
          }
          onClick={handleDownloadPdf}
          variant="contained"
          color="secondary"
          disabled={downloadingPdf}
        >
          {downloadingPdf ? 'Preparing PDF…' : 'Download PDF'}
        </Button>
        </Box>
      </Box>

      <Paper
        id={FORM_PRINT_ROOT_ID}
        elevation={0}
        sx={{
          overflow: 'auto',
          bgcolor: form.surface,
          color: form.text,
          borderRadius: 0,
          border: form.border,
          maxWidth: 980,
          mx: 'auto',
          width: '100%',
          boxShadow: isDark
            ? `0 0 0 1px ${alpha('#fff', 0.04)}, 0 8px 28px ${alpha('#000', 0.45)}`
            : 'none',
          '@media print': {
            border: 'none',
            maxWidth: '100%',
            overflow: 'visible',
            bgcolor: '#fff !important',
            color: '#111 !important',
            boxShadow: 'none',
          },
        }}
      >
        <Box
          sx={{
            bgcolor: form.banner,
            color: form.bannerText,
            px: { xs: 1.5, sm: 2.5 },
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
              '@media print': reportHeaderPrintSx,
          }}
        >
          <Box
            sx={{
              width: { xs: 108, sm: 148 },
              height: { xs: 72, sm: 96 },
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: form.logoBg,
              overflow: 'hidden',
              p: 0,
              border: isDark ? `1px solid ${alpha('#fff', 0.2)}` : 'none',
            }}
          >
            {tenantLogoSrc ? (
              <Box
                component="img"
                src={tenantLogoSrc}
                alt={`${tenantDisplayName} logo`}
                sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            ) : (
              <Typography sx={{ color: HEADER_BLUE, fontWeight: 800, fontSize: '0.7rem', textAlign: 'center' }}>
                LOGO
              </Typography>
            )}
          </Box>
          <Box flex={1} minWidth={140}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.15rem', sm: '1.5rem' },
                letterSpacing: 0.2,
                lineHeight: 1.25,
                color: form.bannerText,
              }}
            >
              {tenantDisplayName}
            </Typography>
          </Box>
          <Box textAlign={{ xs: 'left', sm: 'right' }} flexShrink={0}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '0.95rem', sm: '1.15rem' },
                letterSpacing: 0.4,
                lineHeight: 1.25,
                color: form.bannerText,
              }}
            >
              Access Administration
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', mt: 0.35, opacity: 0.92, color: form.bannerText }}>
              SR AP1
            </Typography>
          </Box>
        </Box>

        <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
          <TableBody>
            <TableRow>
              <FieldCell label="Request Date:" border={form.border} muted={form.muted} sx={{ width: '34%' }}>
                {formatDate(record.requestDate) || '—'}
              </FieldCell>
              <FieldCell label="Request Type:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {formatEnumLabel(record.requestType) || '—'}
              </FieldCell>
              <FieldCell label="Status:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {formatEnumLabel(record.requestStatus) || '—'}
              </FieldCell>
            </TableRow>
            <TableRow>
              <FieldCell label="Requested By:" border={form.border} muted={form.muted} colSpan={2}>
                {dash(record.requestedBy) || '—'}
                {record.requestedByEmail ? ` (${record.requestedByEmail})` : ''}
              </FieldCell>
              <FieldCell label="Updated At:" border={form.border} muted={form.muted}>
                {record.updatedAt ? new Date(record.updatedAt).toLocaleString() : '—'}
              </FieldCell>
            </TableRow>
            <TableRow>
              <FieldCell label="Reason for Request:" border={form.border} muted={form.muted} colSpan={3}>
                <Box sx={{ whiteSpace: 'pre-wrap' }}>{dash(record.reasonForRequest) || '—'}</Box>
              </FieldCell>
            </TableRow>
          </TableBody>
        </Table>

        <Box data-pdf-keep="true" sx={reportKeepTogetherSx}>
        <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  border: form.border,
                  fontWeight: 800,
                  fontSize: '0.78rem',
                  bgcolor: form.headBg,
                  color: 'inherit',
                  py: 1,
                  '@media print': {
                    bgcolor: '#f5f5f5 !important',
                    color: '#111 !important',
                    border: '1px solid #1a1a1a !important',
                  },
                }}
              >
                Approval
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell
                sx={{
                  border: form.border,
                  verticalAlign: 'top',
                  minHeight: 88,
                  py: 1.25,
                  color: 'inherit',
                }}
              >
                {renderApprovalCell()}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Box
          sx={{
            borderTop: form.border,
            py: 1.25,
            px: 1.5,
            textAlign: 'center',
            fontSize: '0.75rem',
            color: form.muted,
            '@media print': { color: '#333 !important', borderTop: '1px solid #1a1a1a !important' },
          }}
        >
          Please retain this form for audit and compliance.
        </Box>
        </Box>
      </Paper>

      <DeploymentStageApprovalDialog
        open={dialogOpen}
        onClose={() => (saving ? null : setDialogOpen(false))}
        onSubmit={handleApprovalSubmit}
        stageStatus="ACCESS_PROVISION"
        slot={dialogSlot}
        currentUser={currentUser}
        saving={saving}
        existingComment=""
        title={`${slotRole(dialogSlot)} — Access Administration`}
        description="Approve this access request, or decline it and leave a comment."
      />
    </Box>
  );
}
